<section class=" relative overflow-hidden" aria-labelledby="pricing-heading">
  <!-- Background decorative elements -->
  <div class="absolute inset-0 overflow-hidden opacity-70 pointer-events-none">
    <div class="absolute w-full h-full">
      <div class="absolute top-0 mx-auto inset-x-0 w-48 h-32 bg-white rounded-full blur-[100px]"></div>
      <div class="absolute bottom-20 right-10 w-48 h-48 bg-electric-blue rounded-full blur-[100px]"></div>
      <div class="absolute bottom-20 left-10 w-48 h-48 bg-magenta rounded-full blur-[100px]"></div>
    </div>
  </div>

  <div class="container mx-auto px-4 ">
    <!-- Section Heading -->
    <div class="text-center mb-12" data-aos="fade-up">
      <h2 id="pricing-heading" class="text-3xl md:text-4xl font-bold mb-4 text-white">Flexible pricing for every need</h2>
      <p class="text-xl text-white/70 max-w-3xl mx-auto">Select a service category below to view detailed pricing</p>
    </div>

    <!-- Category Navigation with Mobile Swipe -->
    <div class="flex flex-col items-center mb-10">
      <p class="text-white/70 mb-3 text-center">Choose a service category:</p>

      <!-- Desktop view (flex-wrap) -->
      <div class="hidden lg:flex lg:flex-wrap justify-center gap-2 mb-2">
        @for (category of categories; track category) {
          <button (click)="setActiveCategory(category)"
                  [ngClass]="{'bg-electric-blue text-dark-blue': category === activeCategory, 'bg-dark-gray text-white/70 hover:text-white': category !== activeCategory}"
                  class="px-4 py-2 rounded-lg text-sm font-medium transition-all">
            {{ category }}
          </button>
        }
      </div>

      <!-- Mobile view (horizontal scroll) -->
      <div class="lg:hidden w-full relative">
        <!-- Left scroll indicator shadow -->
        <div
            class="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-dark-blue to-transparent pointer-events-none z-10"></div>

        <!-- Horizontally scrollable container -->
        <div class="overflow-x-auto mobile-scroll-tabs pb-1">
          <div class="flex gap-2 px-4 min-w-max">
            @for (category of categories; track category) {
              <button (click)="setActiveCategory(category)"
                      [ngClass]="{'bg-electric-blue text-dark-blue': category === activeCategory, 'bg-dark-gray text-white/70 hover:text-white': category !== activeCategory}"
                      class="px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex-shrink-0">
                {{ category }}
              </button>
            }
          </div>
        </div>

        <!-- Right scroll indicator shadow -->
        <div
            class="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-dark-blue to-transparent pointer-events-none z-10"></div>
      </div>

      @if (activeCategory) {
        <p class="text-sm text-electric-blue mt-2">Viewing pricing for: <span
            class="font-semibold">{{ activeCategory }}</span></p>
      }
    </div>

    <!-- Pricing tiers -->
    <div class="grid grid-cols-1 xl:grid-cols-3 gap-8 items-stretch">
      <!-- Pay As You Go Plan -->
      <article class="pricing-card" data-aos="fade-up" data-aos-delay="0">
        <div
            class="relative p-2 md:p-4 bg-dark-gray rounded-2xl border border-white/10 overflow-hidden h-[700px] flex flex-col">
          <!-- Title and description -->
          <div class="mb-6">
            <h3 class="text-xl font-bold text-white mb-4">Pay As You Go</h3>
            <p class="text-white/70 text-sm">Perfect for getting started or for projects with variable needs.
              Transparent per-unit pricing for maximum flexibility.</p>
          </div>
          <!-- Module list for selected category -->
          @if (activeCategory && getItemsByCategory(activeCategory).length > 0) {
            <div class="flex-1 flex flex-col min-h-0">
              <h4 class="text-lg font-semibold text-white/90 mb-3">{{ activeCategory }} Pricing</h4>
              <!-- Module items -->
              <div class="flex-1 min-h-0 mb-8">
                <ul class="space-y-4 max-h-full overflow-y-auto pr-2">
                  @for (item of getItemsByCategory(activeCategory); track item) {
                    <li class="border-b border-white/10 pb-3">
                      <div class="flex justify-between items-start mb-1">
                        <span class="text-sm font-medium text-white/90">{{ item.moduleName || 'Base Price' }}</span>
                        <span class="text-sm font-bold text-electric-blue">{{ item.payAsYouGo }}</span>
                      </div>
                      @if (item.details) {
                        <p class="text-xs text-white/60">{{ item.details }}</p>
                      }
                    </li>
                  }
                </ul>
              </div>
            </div>
          }
          <!-- No data message -->
          @if (!activeCategory || getItemsByCategory(activeCategory).length === 0) {
            <div class="flex-1 flex items-center justify-center mb-8">
              <p class="text-white/60 text-sm">Please select a service category to view pricing details.</p>
            </div>
          }
          <!-- Key Features -->
          <ul class="space-y-3 mb-8 mt-auto">
            <li class="flex items-start">
              <svg class="h-5 w-5 text-electric-blue mr-2 mt-0.5 shrink-0" fill="none"
                   stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
              <span class="text-sm text-white/80">Access to core MyPart modules</span>
            </li>
            <li class="flex items-start">
              <svg class="h-5 w-5 text-electric-blue mr-2 mt-0.5 shrink-0" fill="none"
                   stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
              <span class="text-sm text-white/80">No monthly commitment</span>
            </li>
            <li class="flex items-start">
              <svg class="h-5 w-5 text-electric-blue mr-2 mt-0.5 shrink-0" fill="none"
                   stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
              <span class="text-sm text-white/80">Ideal for low-volume usage</span>
            </li>
          </ul>
          <!-- CTA -->
          <div>
            <button (click)="openApiAccess()" class="w-full btn btn-secondary" type="button">
              Get Started
            </button>
          </div>
        </div>
      </article>
      <!-- Premium Plan -->
      <article class="pricing-card" data-aos="fade-up" data-aos-delay="100">
        <div
            class="relative p-2 md:p-4 bg-gradient-to-br from-dark-blue via-dark-blue to-magenta/20 rounded-2xl border border-electric-blue/30 overflow-hidden h-[700px] flex flex-col">
          <!-- Popular tag -->
          <div class="absolute top-0 right-0">
            <div
                class="bg-gradient-to-r from-electric-blue to-magenta text-dark-blue text-xs font-medium py-1 px-3 rounded-bl-lg">
              Popular
            </div>
          </div>
          <!-- Title and description -->
          <div class="mb-6">
            <h3 class="text-xl font-bold text-white mb-4">Premium</h3>
            <p class="text-white/70 text-sm">Best value for consistent usage. Benefit from lower per-unit rates,
              additional features, and higher throughput.</p>
          </div>
          <!-- Static Premium pricing info -->
          <div class="mb-6">
            <div class="text-center mb-3">
              <h4 class="text-sm font-bold text-white mb-1">Monthly Base Fee Discounts</h4>
              <p class="text-xs text-white/70">Pay a monthly base fee and get discounted rates</p>
            </div>
            <div class="space-y-2">
              <!-- $1,000 Tier -->
              <div class="bg-electric-blue/20 rounded-lg p-2 border border-electric-blue/40">
                <div class="flex justify-between items-center mb-0.5">
                  <span class="text-sm font-bold text-white">$1,000</span>
                  <span
                      class="text-sm font-bold bg-gradient-to-r from-electric-blue to-magenta bg-clip-text text-transparent">5% off</span>
                </div>
                <p class="text-xs text-white/60">Perfect for growing businesses</p>
              </div>
              <!-- $2,500 Tier -->
              <div class="bg-electric-blue/20 rounded-lg p-2 border border-electric-blue/40">
                <div class="flex justify-between items-center mb-0.5">
                  <span class="text-sm font-bold text-white">$2,500</span>
                  <span
                      class="text-sm font-bold bg-gradient-to-r from-electric-blue to-magenta bg-clip-text text-transparent">10% off</span>
                </div>
                <p class="text-xs text-white/60">Ideal for established companies</p>
              </div>
              <!-- $5,000 Tier -->
              <div class="bg-electric-blue/20 rounded-lg p-2 border border-electric-blue/40">
                <div class="flex justify-between items-center mb-0.5">
                  <span class="text-sm font-bold text-white">$5,000</span>
                  <span
                      class="text-sm font-bold bg-gradient-to-r from-electric-blue to-magenta bg-clip-text text-transparent">15% off</span>
                </div>
                <p class="text-xs text-white/60">Great for high-volume usage</p>
              </div>
              <!-- $10,000 Tier -->
              <div class="bg-electric-blue/20 rounded-lg p-2 border border-electric-blue/40">
                <div class="flex justify-between items-center mb-0.5">
                  <span class="text-sm font-bold text-white">$10,000</span>
                  <span
                      class="text-sm font-bold bg-gradient-to-r from-electric-blue to-magenta bg-clip-text text-transparent">20% off</span>
                </div>
                <p class="text-xs text-white/60">Maximum savings for enterprises</p>
              </div>
            </div>
          </div>
          <!-- Key Features -->
          <ul class="space-y-3 mb-8 mt-auto">
            <li class="flex items-start">
              <svg class="h-5 w-5 text-electric-blue mr-2 mt-0.5 shrink-0" fill="none"
                   stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
              <span class="text-sm text-white/80">Discounted per-unit rates</span>
            </li>
            <li class="flex items-start">
              <svg class="h-5 w-5 text-electric-blue mr-2 mt-0.5 shrink-0" fill="none"
                   stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
              <span class="text-sm text-white/80">Access to advanced features</span>
            </li>
            <li class="flex items-start">
              <svg class="h-5 w-5 text-electric-blue mr-2 mt-0.5 shrink-0" fill="none"
                   stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
              <span class="text-sm text-white/80">Higher API call limits</span>
            </li>
            <li class="flex items-start">
              <svg class="h-5 w-5 text-electric-blue mr-2 mt-0.5 shrink-0" fill="none"
                   stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
              <span class="text-sm text-white/80">Optional base subscription</span>
            </li>
          </ul>
          <!-- CTA -->
          <div>
            <button (click)="openApiAccess()" class="w-full btn btn-primary" type="button">
              Get Started
            </button>
          </div>
        </div>
      </article>
      <!-- Enterprise Plan -->
      <article class="pricing-card" data-aos="fade-up" data-aos-delay="200">
        <div
            class="relative p-2 md:p-4 bg-dark-gray rounded-2xl border border-white/10 overflow-hidden h-[700px] flex flex-col">
          <!-- Title and price -->
          <div class="mb-6">
            <h3 class="text-xl font-bold text-white mb-4">Enterprise</h3>
            <p class="text-white/70 text-sm">Custom-built solutions for high-volume demands, specific integration
              needs, and dedicated support.</p>
          </div>
          <!-- Static Enterprise pricing info -->
          <div class="mb-8">
            <div
                class="bg-gradient-to-br from-white/10 to-white/5 rounded-xl p-6 border border-white/20 backdrop-blur-sm">
              <div class="text-center">
                <div class="mb-4">
                  <svg class="w-12 h-12 text-white/80 mx-auto mb-3" fill="none" stroke="currentColor"
                       viewBox="0 0 24 24">
                    <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" stroke-linecap="round" stroke-linejoin="round"
                          stroke-width="1.5"></path>
                  </svg>
                  <h4 class="text-lg font-bold text-white mb-2">Custom Pricing</h4>
                  <p class="text-xs text-white/70 mb-3">All modules included with volume discounts, custom integrations
                    and dedicated support</p>
                </div>
                <div class="bg-white/10 rounded-lg p-4 border border-white/20">
                  <p class="text-sm font-medium text-white/90 mb-2">Tailored Pricing</p>
                  <p class="text-xs text-white/70">Contact us for additional tiers of discounts</p>
                </div>
              </div>
              <!-- Enterprise Features -->
              <div class="grid grid-cols-3 gap-2 mt-6">
                <div class="bg-white/10 rounded-lg p-1 border border-white/20 flex items-center justify-center">
                  <p class="text-xs font-medium text-white/90">Volume discounts</p>
                </div>
                <div class="bg-white/10 rounded-lg p-1 border border-white/20 flex items-center justify-center">
                  <p class="text-xs font-medium text-white/90">Custom SLA's</p>
                </div>
                <div class="bg-white/10 rounded-lg p-1 border border-white/20 flex items-center justify-center">
                  <p class="text-xs font-medium text-white/90">Dedicated Support</p>
                </div>
              </div>
            </div>
          </div>
          <!-- Key Features -->
          <ul class="space-y-3 mb-8 mt-auto">
            <li class="flex items-start">
              <svg class="h-5 w-5 text-electric-blue mr-2 mt-0.5 shrink-0" fill="none"
                   stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
              <span class="text-sm text-white/80">Volume-based pricing</span>
            </li>
            <li class="flex items-start">
              <svg class="h-5 w-5 text-electric-blue mr-2 mt-0.5 shrink-0" fill="none"
                   stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
              <span class="text-sm text-white/80">Custom integrations & SLAs</span>
            </li>
            <li class="flex items-start">
              <svg class="h-5 w-5 text-electric-blue mr-2 mt-0.5 shrink-0" fill="none"
                   stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
              <span class="text-sm text-white/80">Dedicated account management</span>
            </li>
            <li class="flex items-start">
              <svg class="h-5 w-5 text-electric-blue mr-2 mt-0.5 shrink-0" fill="none"
                   stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
              <span class="text-sm text-white/80">Full catalog support</span>
            </li>
          </ul>
          <!-- CTA -->
          <div class="contact-dropdown-container">
            <div [class.show]="contactDropdownOpen['enterprise']" class="contact-dropdown contact-dropdown-above">
              <button (click)="copyEmail()" class="dropdown-item" type="button">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      stroke-linecap="round" stroke-linejoin="round"
                      stroke-width="2"></path>
                </svg>
                Copy Email
              </button>
              <button (click)="openEmailApp()" class="dropdown-item" type="button">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      stroke-linecap="round" stroke-linejoin="round"
                      stroke-width="2"></path>
                </svg>
                Open Email App
              </button>
            </div>

            <button (click)="toggleContactDropdown('enterprise')" class="w-full btn btn-secondary" type="button">
              Contact Sales
            </button>
          </div>
        </div>
      </article>
    </div>

    <!-- FAQ link -->
    <div class="mt-12 text-center" data-aos="fade-up" data-aos-delay="300">
      <p class="text-white/70">
        Have questions about our pricing?
        <a class="text-electric-blue hover:underline" href="mailto:demo&#64;mypart.com">Contact our sales team</a>
      </p>
    </div>
  </div>
</section>
